<template>
  <div
    ref="option"
    class="option"
    role="option"
    tabindex="-1"
    :aria-selected="String(selected)"
    :data-cy="`user-add-option-${dataCy}`"
    @click="emit('optionClicked')"
    @keydown.enter.prevent="emit('optionClicked')"
    @keydown.space.prevent="emit('optionClicked')"
  >
    <div class="option-title">
      <slot name="title" />
    </div>
    <div class="option-details">
      <slot name="details" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'

const props = withDefaults(
  defineProps<{ dataCy?: string | number; selected?: boolean }>(),
  {
    dataCy: '',
    selected: false,
  }
)

const emit = defineEmits<{
  (event: 'optionClicked'): void
}>()

const option = ref<HTMLElement | null>(null)

// An option knows when it becomes the active one, so keeping itself in view is
// its own concern — the listbox would otherwise have to find it in the DOM.
watch(
  () => props.selected,
  async (isSelected) => {
    if (!isSelected) return
    await nextTick()
    option.value?.scrollIntoView({ block: 'nearest' })
  }
)
</script>

<style lang="scss">
.option {
  min-width: 0;
  padding: $spacing-4xs $spacing-xs;
  cursor: pointer;

  &:hover,
  &[aria-selected='true'] {
    background-color: $color-pebble;
  }
  .option-title {
    @include subtitle-2;
    @include ellipsis;
    font-size: $font-size-s;
  }
  .option-details {
    margin-top: $spacing-5xs;
    display: flex;
    justify-content: space-between;
    gap: $spacing-xs;
    min-width: 0;
    font-size: $font-size-s;
    color: $color-text-light;

    span {
      @include ellipsis;
      min-width: 0;
    }
  }
}
</style>
